/**
 * src/types.ts
 *
 * Mirror of backend/product/src/modules/real_browsers/real_browsers.types.ts,
 * with Date fields downgraded to ISO string (what JSON over the wire gives us).
 *
 * Keep this in sync with the backend — when adding fields, add them in both
 * places. The shared product README explains why we duplicate rather than
 * extracting a `frontend/_shared/` package: doing so adds tooling for
 * exactly two consumers (observe + real-browsers), which isn't worth it yet.
 */

export type ProviderKind =
  | 'remote_vm'
  | 'cloud'
  | 'home_device'
  | 'mobile_device'
  | 'baremetal';

export interface BrowserProvider {
  id: string;
  display_name: string;
  kind: ProviderKind;
  region?: string;
  enabled: boolean;
  notes?: string;
  updated_at: string;
}

export type BrowserKind = 'chrome' | 'firefox' | 'safari' | 'mobile_chrome';
export type Health = 'healthy' | 'degraded' | 'critical' | 'unknown';

export interface BrowserPool {
  id: string;
  provider_id: string;
  display_name: string;
  enabled: boolean;
  browser: BrowserKind;
  vnc_endpoint?: string;
  capacity: number;
  active_tabs: number;
  health: Health;
  updated_at: string;
}

export type InstanceStatus = 'healthy' | 'degraded' | 'dead' | 'unknown';

export interface BrowserInstance {
  id: string;
  pool_id: string;
  provider_id: string;
  cdp_host: string;
  cdp_port: number;
  vnc_endpoint?: string;
  status: InstanceStatus;
  enabled: boolean;
  concurrent_tabs: number;
  active_tabs: number;
  total_tabs_24h?: number;
  block_rate_24h?: number;
  last_heartbeat_at?: string;
  notes?: string;
  updated_at: string;
}

export type ActionKind =
  | 'screenshot'
  | 'navigate'
  | 'click'
  | 'scroll'
  | 'type'
  | 'keypress'
  | 'mouse'
  | 'pause'
  | 'resume'
  | 'reload_tab'
  | 'vnc_connect'
  | 'vnc_disconnect';

export interface BrowserAction {
  id: string;
  ts: string;
  instance_id: string;
  actor: string;
  kind: ActionKind;
  detail?: Record<string, unknown>;
  outcome: 'ok' | 'failed';
  error?: string;
}

export interface CdpTab {
  id: string;
  type: string;
  title: string;
  url: string;
  webSocketDebuggerUrl: string;
}

export interface AlertRecord {
  id: string;
  ts: string;
  category: string;
  severity: 'info' | 'warn' | 'error' | 'critical';
  message: string;
  context?: Record<string, unknown>;
  fingerprint?: string;
  count?: number;
}

export const PROVIDER_KINDS: ProviderKind[] = [
  'remote_vm', 'cloud', 'home_device', 'mobile_device', 'baremetal',
];
